import type { RowData } from "@tanstack/table-core";
import type { ReactNode } from "react";

/**
 * How a list table's columns are sized: each column says what kind of value it holds, and its kind
 * decides how its width is found (#6708).
 *
 * The rules, in the order the fit applies them:
 *
 * - **A column's minimum is its heading**: the label, the cell's padding, and 42px for the sort arrow
 *   and the column menu, which show on hover (`headerToolsOnHoverSx`). A heading never clips or wraps,
 *   because no column is ever narrower than this. Where every minimum does not fit, the table scrolls.
 * - **Names and free text** prefer the 80th percentile of their measured content, capped by the kind's
 *   maximum. Past that width they truncate, and the cell's tooltip carries the rest. The percentile, not
 *   the widest or the average: one long title or a column of blanks should not decide the width.
 * - **Dates, ranges, numbers and money** prefer their widest value, and never truncate: their widest
 *   value is their minimum as well.
 * - **Spare room** over the preferred widths is shared in proportion to them, as MRT's grid layout
 *   shares it, so no one column balloons. Room short of the preferred widths is taken from each column
 *   in proportion to what it asked for over its minimum.
 *
 * - **Short of every minimum, a column folds before the table scrolls** (#6711). A column that names a
 *   host (`meta.fold`) moves onto its host's second line, the lowest priority first, until the minimums
 *   fit. The table scrolls sideways only once nothing is left to fold. A folded column is still shown:
 *   Show/hide lists it as on, and its value reads under its host. #5395 is why a column is never just
 *   hidden for want of room.
 *
 * A column with no kind keeps the width it declares, and takes no share. That is how the select column,
 * the expander and any column a table has not described yet keep working beside the fit.
 *
 * This file is the arithmetic only. `useFittedColumns` measures the DOM and hands the answer to MRT.
 */

export type TableColumnKind =
  | "name"
  | "text"
  | "person"
  | "people"
  | "date"
  | "dateRange"
  | "timestamp"
  | "status"
  | "number"
  | "money"
  | "tags"
  | "actions";

/**
 * Widths a column states for itself, over what its kind would measure.
 *
 * Use these for a column whose content is not a fair measure of what it needs: a cell that draws more
 * as it is given more room (the proposals Team column), or one whose content wraps. A kind's own
 * maximum caps only what the column asks for, and spare room can still widen it; a `maximum` stated
 * here is the widest the column is ever drawn, as the fee schedule Description's is (#5111).
 */
export type TableColumnWidthOverride = {
  minimum?: number;
  preferred?: number;
  maximum?: number;
};

/**
 * Where a column goes when the pane is too narrow for it, and how soon (#6711).
 *
 * `into` is the id of the host column, whose second line the value moves onto. `priority` orders the
 * folding: the lowest folds first. `render` is the value as it reads on the host's line, short and on
 * one line, such as "Fee: Hourly rates 2027"; it answers nothing for a row with no value, so that row's
 * line says nothing. Without it, the line reads the column's heading and its cell.
 */
export type TableColumnFold<TData extends RowData = RowData> = {
  into: string;
  priority: number;
  render?: (row: TData) => ReactNode;
};

/**
 * How many data columns a list shows until the reader chooses more (#6707). A budget, not a cap: every
 * other column stays in Show/hide, and the values a hidden column held read under another column.
 */
export const DEFAULT_DATA_COLUMN_BUDGET = 6;

declare module "@tanstack/table-core" {
  // `TData` and `TValue` must match TanStack's own declaration for the merge to apply.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** What the column holds, which decides how the list table fits it. See `table-column-fit.ts`. */
    kind?: TableColumnKind;
    /** Widths the column states for itself, over what its kind measures. */
    width?: TableColumnWidthOverride;
    /** The host the column folds onto when the pane is short of room, and when. See {@link TableColumnFold}. */
    fold?: TableColumnFold<TData>;
  }
}

type KindRule = {
  /** Which measure of the loaded rows the column prefers. */
  prefers: "percentile" | "widest";
  /** Whether a cell may cut its content off. One that may not is never narrower than its widest value. */
  truncates: boolean;
  /** The widest the column's preferred width may be. Past it, the content truncates. */
  maximum?: number;
  /** Whether the column takes a share of the room over every preferred width. */
  grows: boolean;
};

export const COLUMN_KIND_RULES: Readonly<Record<TableColumnKind, KindRule>> = {
  name: { prefers: "percentile", truncates: true, maximum: 360, grows: true },
  text: { prefers: "percentile", truncates: true, maximum: 320, grows: true },
  person: { prefers: "percentile", truncates: true, maximum: 240, grows: true },
  people: { prefers: "percentile", truncates: true, maximum: 280, grows: true },
  tags: { prefers: "percentile", truncates: true, maximum: 280, grows: true },
  status: { prefers: "widest", truncates: false, grows: true },
  date: { prefers: "widest", truncates: false, grows: true },
  dateRange: { prefers: "widest", truncates: false, grows: true },
  timestamp: { prefers: "widest", truncates: false, grows: true },
  number: { prefers: "widest", truncates: false, grows: true },
  money: { prefers: "widest", truncates: false, grows: true },
  // Sized to its controls, and nothing more: room it takes is room a value column could have used.
  actions: { prefers: "widest", truncates: false, grows: false },
};

/**
 * What a heading keeps beside its label: the 40px column menu, 2px in from the cell's right-hand edge,
 * which the sort arrow of the column the table is sorted by sits under while the menu shows. Measured
 * in #6545, where "Fixed?" at 80px read as "Fixe⋮" under the pointer.
 */
export const HEADING_TOOLS_WIDTH = 42;

/** Which share of a column's loaded rows a name or a free-text column is sized to show in full. */
export const CONTENT_PERCENTILE = 0.8;

/**
 * The value below which `fraction` of `values` fall, by the nearest-rank method.
 *
 * Nearest rank rather than interpolation, so the answer is always a width some row really has: a column
 * sized to it shows that row's content exactly. Zero for no values.
 */
export function percentile(values: readonly number[], fraction: number): number {
  if (values.length < 1) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const rank = Math.ceil(fraction * sorted.length);
  return sorted[Math.min(sorted.length, Math.max(1, rank)) - 1];
}

/**
 * What was measured of one column in the browser.
 *
 * Every width is the whole cell's, its padding included. `headingWidth` is the label and the heading's
 * padding, without the room for its tools; zero where nothing could be measured, which is every
 * renderer without layout.
 */
export type ColumnMeasurement = {
  headingWidth: number;
  contentWidths: readonly number[];
};

/** What the fit is asked about one column. */
export type FitColumn = {
  id: string;
  minimum: number;
  preferred: number;
  grows: boolean;
  /** The widest the column is drawn, whatever room is spare: its stated maximum, or MRT's `maxSize`. */
  limit?: number;
  /** The host the column folds onto, and how soon. A column without one never folds. */
  fold?: { into: string; priority: number };
};

/** What the fold leaves: the columns still drawn, in their order, and the host each folded column went to. */
export type FoldedColumns = {
  shown: readonly FitColumn[];
  foldedInto: Readonly<Record<string, string>>;
};

/**
 * Which columns fold onto their hosts' second lines for every minimum to fit in `availableWidth`.
 *
 * While the minimums are wider than the pane, the column with the lowest priority among those that can
 * fold goes; on a tie, the one further right. A column can fold only while its host is drawn, and a host
 * that holds a folded value does not fold itself, so a value is never two moves from where it belongs.
 * When nothing is left to fold, the rest stay and the table scrolls. An unmeasured pane folds nothing.
 */
export function foldColumns(availableWidth: number | undefined, columns: readonly FitColumn[]): FoldedColumns {
  const foldedInto: Record<string, string> = {};
  if (availableWidth === undefined || availableWidth <= 0) return { shown: columns, foldedInto };

  let shown = [...columns];
  while (sum(shown.map((column) => column.minimum)) > availableWidth) {
    const hosts = new Set(Object.values(foldedInto));
    const shownIds = new Set(shown.map((column) => column.id));
    let next: FitColumn | undefined;
    for (const column of shown) {
      if (!column.fold || !shownIds.has(column.fold.into) || hosts.has(column.id)) continue;
      if (next === undefined || column.fold.priority <= (next.fold?.priority ?? 0)) next = column;
    }
    if (!next?.fold) break;
    foldedInto[next.id] = next.fold.into;
    const folding = next;
    shown = shown.filter((column) => column !== folding);
  }
  return { shown, foldedInto };
}

/**
 * The narrowest, preferred and growing behavior of one column of a kind, from what was measured of it.
 *
 * A column whose heading measured nothing falls back on the widths it declares, so in a renderer with
 * no layout the table is drawn exactly as its columns ask.
 */
export function columnBounds(
  params: Readonly<{
    id: string;
    kind: TableColumnKind;
    override?: TableColumnWidthOverride;
    measurement: ColumnMeasurement;
    declared: { size: number; minSize: number };
  }>,
): FitColumn {
  const { id, kind, override, measurement, declared } = params;
  const rule = COLUMN_KIND_RULES[kind];
  const isMeasured = measurement.headingWidth > 0 || measurement.contentWidths.some((width) => width > 0);
  if (!isMeasured) {
    const minimum = override?.minimum ?? declared.minSize;
    return { id, minimum, preferred: Math.max(minimum, override?.preferred ?? declared.size), grows: rule.grows };
  }

  // A heading the column hides, as the actions column does, asks for no room of its own.
  const heading = kind === "actions" ? 0 : Math.ceil(measurement.headingWidth) + HEADING_TOOLS_WIDTH;
  const widest = Math.ceil(Math.max(0, ...measurement.contentWidths));
  const measured =
    rule.prefers === "percentile" ? Math.ceil(percentile(measurement.contentWidths, CONTENT_PERCENTILE)) : widest;

  // MRT builds the actions column itself, so the declared minimum of one collapsed button is drawn
  // whatever the fit says. The fit counts it, or the other columns take room the table does not have.
  const floor = kind === "actions" ? declared.minSize : 0;
  const minimum = Math.max(heading, floor, override?.minimum ?? 0, rule.truncates ? 0 : widest);
  const maximum = override?.maximum ?? rule.maximum ?? Number.POSITIVE_INFINITY;
  const preferred = Math.max(minimum, Math.min(override?.preferred ?? measured, maximum));
  const limit = override?.maximum === undefined ? undefined : Math.max(minimum, override.maximum);
  return { id, minimum, preferred, grows: rule.grows, ...(limit === undefined ? {} : { limit }) };
}

/**
 * Each column's width in `availableWidth`, in pixels.
 *
 * - No more room than the minimums: every column at its minimum, and the table scrolls.
 * - Between the minimums and the preferred widths: each column gets its minimum, and the room left is
 *   shared in proportion to how much more than its minimum each asked for. A column that asked for no
 *   more than its minimum is not narrowed under it.
 * - More than the preferred widths: each column gets its preferred width, and the rest is shared among
 *   the growing columns in proportion to those widths.
 *
 * Widths are whole pixels, and the pixels rounding leaves over go to the widest growing column, so the
 * columns fill the pane exactly rather than leaving a sliver at the right. An unmeasured pane sizes
 * nothing: the first render, and every renderer without layout.
 */
export function fitColumnWidths(
  availableWidth: number | undefined,
  columns: readonly FitColumn[],
): Record<string, number> {
  if (availableWidth === undefined || availableWidth <= 0 || columns.length < 1) return {};

  const totalMinimum = sum(columns.map((column) => column.minimum));
  if (availableWidth <= totalMinimum) return Object.fromEntries(columns.map((column) => [column.id, column.minimum]));

  const totalPreferred = sum(columns.map((column) => column.preferred));
  const exact =
    availableWidth <= totalPreferred
      ? shareTowardPreferred(columns, availableWidth - totalMinimum)
      : shareOverPreferred(columns, availableWidth - totalPreferred);

  const widths: Record<string, number> = {};
  for (const column of columns) widths[column.id] = Math.floor(exact[column.id]);

  const receiver = widestGrowing(columns, widths);
  if (receiver) widths[receiver] += availableWidth - sum(Object.values(widths));
  return widths;
}

function shareTowardPreferred(columns: readonly FitColumn[], room: number): Record<string, number> {
  const totalWanted = sum(columns.map((column) => column.preferred - column.minimum));
  return Object.fromEntries(
    columns.map((column) => [column.id, column.minimum + ((column.preferred - column.minimum) / totalWanted) * room]),
  );
}

function shareOverPreferred(columns: readonly FitColumn[], spare: number): Record<string, number> {
  const widths: Record<string, number> = Object.fromEntries(columns.map((column) => [column.id, column.preferred]));
  // Shared in rounds: a column that reaches its limit takes no more, and its share goes to the rest.
  let growing = columns.filter((column) => column.grows && hasRoom(column, column.preferred));
  let room = spare;
  while (room > 0.5 && growing.length > 0) {
    const totalGrowing = sum(growing.map((column) => column.preferred));
    if (totalGrowing <= 0) break;
    let given = 0;
    for (const column of growing) {
      const share = (column.preferred / totalGrowing) * room;
      const width = Math.min(widths[column.id] + share, column.limit ?? Number.POSITIVE_INFINITY);
      given += width - widths[column.id];
      widths[column.id] = width;
    }
    room -= given;
    growing = growing.filter((column) => hasRoom(column, widths[column.id]));
  }
  return widths;
}

function hasRoom(column: FitColumn, width: number): boolean {
  return column.limit === undefined || width < column.limit;
}

function widestGrowing(columns: readonly FitColumn[], widths: Readonly<Record<string, number>>): string | undefined {
  let widest: string | undefined;
  for (const column of columns) {
    if (!column.grows || !hasRoom(column, widths[column.id])) continue;
    if (widest === undefined || widths[column.id] > widths[widest]) widest = column.id;
  }
  return widest;
}

function sum(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}
